/**
 * Smart Contract ABIs (Unified Naming)
 * 
 * 命名策略：
 * - 文件名：统一无版本号（如 BLSAggregator.json）
 * - 版本信息：通过 ABI 中的 version() 函数获取
 * - 同步时自动去除版本号后缀（V3 等）
 */

// Core System
import RegistryABIData from './Registry.json' with { type: 'json' };
import GTokenABIData from './GToken.json' with { type: 'json' };
import GTokenAuthorizationABIData from './GTokenAuthorization.json' with { type: 'json' };
import GTokenStakingABIData from './GTokenStaking.json' with { type: 'json' };
import SuperPaymasterABIData from './SuperPaymaster.json' with { type: 'json' };
import PaymasterFactoryABIData from './PaymasterFactory.json' with { type: 'json' };
import PaymasterABIData from './Paymaster.json' with { type: 'json' };
import LivenessRegistryABIData from './LivenessRegistry.json' with { type: 'json' };
import AAStarGlobalGuardABIData from './AAStarGlobalGuard.json' with { type: 'json' };
import RailgunParserABIData from './RailgunParser.json' with { type: 'json' };
import UniswapV3ParserABIData from './UniswapV3Parser.json' with { type: 'json' };

// AA Standard (从 out/ 提取)
import EntryPointABIData from './EntryPoint.json' with { type: 'json' };
import SimpleAccountABIData from './SimpleAccount.json' with { type: 'json' };
import SimpleAccountFactoryABIData from './SimpleAccountFactory.json' with { type: 'json' };
// AirAccount V7 (diamond-lite) — MERGED full ABI: account + fallback-routed agent/weight
// functions. Generated by `scripts/build-full-abi.mjs` in airaccount-contract. Do NOT replace
// with the raw out/AAStarAirAccountV7.json ABI: agent (ERC-8004) + weight-governance functions
// execute via fallback->delegatecall and are absent from the raw account ABI. See contract repo
// docs/2026-05-26-diamond-lite-migration-impact.md.
import AAStarAirAccountV7ABIData from './AAStarAirAccountV7.json' with { type: 'json' };

// Token System
import xPNTsTokenABIData from './xPNTsToken.json' with { type: 'json' };
import xPNTsFactoryABIData from './xPNTsFactory.json' with { type: 'json' };
import MySBTABIData from './MySBT.json' with { type: 'json' };

// Agent Economy (SP V5.3)
import MicroPaymentChannelABIData from './MicroPaymentChannel.json' with { type: 'json' };

// Identity & Reputation
import ReputationSystemABIData from './ReputationSystem.json' with { type: 'json' };

// Monitoring System
import DVTValidatorABIData from './DVTValidator.json' with { type: 'json' };
import BLSAggregatorABIData from './BLSAggregator.json' with { type: 'json' };
import BLSValidatorABIData from './BLSValidator.json' with { type: 'json' };

// DVT layer-1 account policy (SuperPaymaster v5.4) — sender-keyed, governance-gated
// trigger/spend policy read by staked consumers during validation. Asymmetric lifecycle:
// tighten/freeze are immediate (guardian or timelock); loosen/unfreeze flow through an
// EXTERNAL OZ TimelockController (2-day minDelay). See actions/policyRegistry.ts.
import PolicyRegistryABIData from './PolicyRegistry.json' with { type: 'json' };

// EXTERNAL OZ TimelockController (standard @openzeppelin/contracts). The governance
// controller that multisig/timelock-gated admin setters (e.g. BLSAggregator
// setSlashPolicyAdmin / setSlashThreshold) are routed through — schedule() → wait
// minDelay → execute(). See actions/timelockController.ts + admin SlashGovernance (CC-13).
import TimelockControllerABIData from './TimelockController.json' with { type: 'json' };

// x402 settlement facilitator (SuperPaymaster v5.4) — on-chain settle path for the x402
// micropayment protocol. Verifies EIP-3009 (transferWithAuthorization) / direct-settle
// authorizations and routes operator/facilitator fees. Sepolia: 0xFe95a77e...
// See deployments/config.sepolia.json (key `x402Facilitator`) + addresses.ts.
import X402FacilitatorABIData from './X402Facilitator.json' with { type: 'json' };

// ========== Re-export ABIs - Core System ==========
export const RegistryABI = (RegistryABIData as any).abi || RegistryABIData;
export const RegistryArtifact = RegistryABIData;

// LivenessRegistry (SuperPaymaster CC-29 DVT liveness layer). Node heartbeat / liveness-window reads
// used by DVT peer-failover; exposed as an ABI (no wrapper — reached via viem directly).
export const LivenessRegistryABI = (LivenessRegistryABIData as any).abi || LivenessRegistryABIData;
export const LivenessRegistryArtifact = LivenessRegistryABIData;

export const GTokenABI = (GTokenABIData as any).abi || GTokenABIData;
export const GTokenArtifact = GTokenABIData;

// GTokenAuthorization v2.2.0 — EIP-3009 gasless transfers (SuperPaymaster main, 2026-05-21)
// @notice Requires GTokenAuthorization contract deployment. Do NOT use with legacy GToken address.
// @notice MAX_AUTH_VALIDITY = 5 min — keep validBefore - validAfter < 300s.
// @notice receiveWithAuthorization: msg.sender must equal `to` (relay cannot submit).
export const GTokenAuthorizationABI = (GTokenAuthorizationABIData as any).abi || GTokenAuthorizationABIData;
export const GTokenAuthorizationArtifact = GTokenAuthorizationABIData;

export const GTokenStakingABI = (GTokenStakingABIData as any).abi || GTokenStakingABIData;
export const GTokenStakingArtifact = GTokenStakingABIData;

export const SuperPaymasterABI = (SuperPaymasterABIData as any).abi || SuperPaymasterABIData;
export const SuperPaymasterArtifact = SuperPaymasterABIData;

export const PaymasterFactoryABI = (PaymasterFactoryABIData as any).abi || PaymasterFactoryABIData;
export const PaymasterFactoryArtifact = PaymasterFactoryABIData;
export const AAStarGlobalGuardABI = (AAStarGlobalGuardABIData as any).abi || AAStarGlobalGuardABIData;
export const RailgunParserABI = (RailgunParserABIData as any).abi || RailgunParserABIData;
export const UniswapV3ParserABI = (UniswapV3ParserABIData as any).abi || UniswapV3ParserABIData;

// Paymaster V4 (使用 Paymaster.json)
export const PaymasterABI = (PaymasterABIData as any).abi || PaymasterABIData;
export const PaymasterArtifact = PaymasterABIData;

// ========== AA Standard ==========
export const EntryPointABI = (EntryPointABIData as any).abi || EntryPointABIData;
export const EntryPointArtifact = EntryPointABIData;

export const SimpleAccountABI = (SimpleAccountABIData as any).abi || SimpleAccountABIData;
export const SimpleAccountArtifact = SimpleAccountABIData;

export const SimpleAccountFactoryABI = (SimpleAccountFactoryABIData as any).abi || SimpleAccountFactoryABIData;
export const SimpleAccountFactoryArtifact = SimpleAccountFactoryABIData;

// AirAccount V7 (diamond-lite). Full ABI includes the fallback-routed agent + weight-governance
// functions — call them at the ACCOUNT address; the account routes to AirAccountExtension internally.
export const AAStarAirAccountV7ABI = (AAStarAirAccountV7ABIData as any).abi || AAStarAirAccountV7ABIData;
export const AAStarAirAccountV7Artifact = AAStarAirAccountV7ABIData;

// ========== Token System ==========
export const xPNTsTokenABI = (xPNTsTokenABIData as any).abi || xPNTsTokenABIData;
export const xPNTsTokenArtifact = xPNTsTokenABIData;

export const xPNTsFactoryABI = (xPNTsFactoryABIData as any).abi || xPNTsFactoryABIData;
export const xPNTsFactoryArtifact = xPNTsFactoryABIData;

export const MySBTABI = (MySBTABIData as any).abi || MySBTABIData;
export const MySBTArtifact = MySBTABIData;

// ========== Agent Economy (SP V5.3) ==========
export const MicroPaymentChannelABI = (MicroPaymentChannelABIData as any).abi || MicroPaymentChannelABIData;
export const MicroPaymentChannelArtifact = MicroPaymentChannelABIData;

// ========== Identity & Reputation ==========
export const ReputationSystemABI = (ReputationSystemABIData as any).abi || ReputationSystemABIData;
export const ReputationSystemArtifact = ReputationSystemABIData;

// ========== Monitoring System ==========
export const DVTValidatorABI = (DVTValidatorABIData as any).abi || DVTValidatorABIData;
export const DVTValidatorArtifact = DVTValidatorABIData;

export const BLSAggregatorABI = (BLSAggregatorABIData as any).abi || BLSAggregatorABIData;
export const BLSAggregatorArtifact = BLSAggregatorABIData;

export const BLSValidatorABI = (BLSValidatorABIData as any).abi || BLSValidatorABIData;
export const BLSValidatorArtifact = BLSValidatorABIData;

// PolicyRegistry (SP v5.4 DVT layer-1). Reads (checkPolicy / getAssetPolicy / getContractScope
// / getAssetSpend / isSelectorAllowed / isFrozen / isAuthorizedConsumer / guardian / timelock)
// + asymmetric writes (immediate tighten/freeze; timelocked loosen/unfreeze).
export const PolicyRegistryABI = (PolicyRegistryABIData as any).abi || PolicyRegistryABIData;
export const PolicyRegistryArtifact = PolicyRegistryABIData;

// EXTERNAL OZ TimelockController — schedule/execute/cancel + getTimestamp/getMinDelay/
// hashOperation/isOperation* (governance orchestration for policy-admin setters, CC-13).
export const TimelockControllerABI = (TimelockControllerABIData as any).abi || TimelockControllerABIData;

// X402Facilitator (SP v5.4). Settlement entrypoint for x402 micropayments: verify +
// settle EIP-3009 authorizations (USDC-native) and direct xPNTs transfers, with
// operator/facilitator fee accounting. Sepolia: 0xFe95a77e4Db593E6EA88000Aad9cD1230BAB4512.
export const X402FacilitatorABI = (X402FacilitatorABIData as any).abi || X402FacilitatorABIData;
export const X402FacilitatorArtifact = X402FacilitatorABIData;


// ========== AirAccount v0.18 stack (synced 2026-06-14) ==========
//
// Source: github.com/AAStarCommunity/airaccount-contract `main` (v0.18).
// ABIs copied from `out/<Contract>.sol/<Contract>.json` (per-contract) and the merged
// `abi/AAStarAirAccountV7.full.json` (account + fallback-routed agent/weight, 153 entries).
//
// v0.18 ABI deltas vs beta.4 (see docs/V0.18-SDK-MIGRATION.md):
//   - AAStarAirAccountFactoryV7: NEW ctor — implementation injected as arg 1 (#82 EIP-3860 fix)
//   - AAStarBLSAlgorithm: + aggregator() / setAggregator() / acceptOwnership() / pendingOwner() (Ownable2Step, #45)
//   - AAStarBLSAggregator: NEW ctor (blsAlgorithm, entryPoint)
//   - AAStarAirAccountV7: + moduleManagementNonce(), pendingModuleInstall() (w/ executeAfter), proposeModuleInstall()
//   - AirAccountExtension: + module-install-timelock surface + ModuleInstall* errors
//
// Sepolia deployment 2026-06-14 (full redeploy — non-upgradable account):
//   AAStarBLSAlgorithm        0x2869EEb04218ca666c6373c0DC5aCDa04F00adFA
//   AAStarValidator (router)  0xe785AF830aD33F3E550FfdC0fEB81D42507DA39D
//   AAStarBLSAggregator       0x9AD55930B77C002dF884F4dac846D2077CDA7C8b
//   SessionKeyValidator       0x82f16163D0fb9c4dd7507b9999B79527a795291C
//   ForceExitModule           0x0F6960526acf4cF9123e0aBc82d7a59fA0B6C934
//   AirAccountDelegate        0x70A8E31c425Ef3F23a2F9E05C48Bd998Aa29085b
//   CalldataParserRegistry    0x5dEE2c5279eFfC7c7FE711233bE42726EE0d4166
//   AAStarAirAccountFactoryV7 0xB14a870e4f63CA21a7EB753588CC4eBFb429E163  (NEW ctor — impl arg 1)
//   AAStarAirAccountV7 (impl) 0x1Bc1119e3Ce4B6D158a6eadb31A06FdcE51992cF
//   AirAccountExtension       0xB1B3acd47DB89806F8431da3452769f1243b4d56
//   AgentRegistry             0x118eD73f22e41cb69282c78b216426D2d98A3935
//
// NOTE on naming overlap with SuperPaymaster: this repo has BOTH
//   - BLSAggregator.json + BLSValidator.json (legacy SuperPaymaster exports above)
//   - AAStarBLSAggregator.json + AAStarBLSAlgorithm.json (THIS section — AirAccount v0.17.2)
// They are NOT the same — AirAccount uses EIP-2537 BLS12-381 pairing primitives.

import AAStarAirAccountFactoryV7ABIData from './AAStarAirAccountFactoryV7.json' with { type: 'json' };
import AgentRegistryABIData from './AgentRegistry.json' with { type: 'json' };
import SessionKeyValidatorABIData from './SessionKeyValidator.json' with { type: 'json' };
import ForceExitModuleABIData from './ForceExitModule.json' with { type: 'json' };
import AirAccountDelegateABIData from './AirAccountDelegate.json' with { type: 'json' };
import AAStarValidatorABIData from './AAStarValidator.json' with { type: 'json' };
import AAStarBLSAlgorithmABIData from './AAStarBLSAlgorithm.json' with { type: 'json' };
// CC-98 / CC-103 per-proposal committee validator (YetAnotherAA-Validator #237). Mounted at
// algId 0x01 on the v0.31.0 router 0xA15127e8… (on-chain verified). Supersedes the whole-set
// AAStarBLSAlgorithm for accounts on that router; the two coexist on different routers.
import AAStarCommitteeValidatorABIData from './AAStarCommitteeValidator.json' with { type: 'json' };
import AAStarBLSAggregatorABIData from './AAStarBLSAggregator.json' with { type: 'json' };
import CalldataParserRegistryABIData from './CalldataParserRegistry.json' with { type: 'json' };
import AirAccountExtensionABIData from './AirAccountExtension.json' with { type: 'json' };

// Generic ERC-20 (payment tokens: USDC / USDT) + MushroomDAO launch token-sale stack
// (aPoints + governance-token sale). Source: MushroomDAO/launch contracts/out/*.json.
import ERC20ABIData from './ERC20.json' with { type: 'json' };
import SaleContractV2ABIData from './SaleContractV2.json' with { type: 'json' };
import APNTsSaleContractABIData from './APNTsSaleContract.json' with { type: 'json' };
import BuyHelperABIData from './BuyHelper.json' with { type: 'json' };

export const AAStarAirAccountFactoryV7ABI = (AAStarAirAccountFactoryV7ABIData as any).abi || AAStarAirAccountFactoryV7ABIData;
export const AAStarAirAccountFactoryV7Artifact = AAStarAirAccountFactoryV7ABIData;

export const AgentRegistryABI = (AgentRegistryABIData as any).abi || AgentRegistryABIData;
export const AgentRegistryArtifact = AgentRegistryABIData;

// v0.17.2-beta.1 unified Session Key (algId 0x08) — replaces deleted AgentSessionKeyValidator +
// AirAccountCompositeValidator + TierGuardHook. Carries both classic single-target sessions and
// agent-grade controls (velocity, callTargets[], selectorAllowlist[]). 19 functions.
export const SessionKeyValidatorABI = (SessionKeyValidatorABIData as any).abi || SessionKeyValidatorABIData;
export const SessionKeyValidatorArtifact = SessionKeyValidatorABIData;

// v0.17.2-beta.3 ForceExitModule — IncompatibleAccount error + assembly _countBits + MODULE_VERSION.
// Per-account ERC-7579 Executor (moduleTypeId=2). Errors: SignerNoLongerGuardian, IncompatibleAccount.
export const ForceExitModuleABI = (ForceExitModuleABIData as any).abi || ForceExitModuleABIData;
export const ForceExitModuleArtifact = ForceExitModuleABIData;

// EIP-7702 delegation target — turns a regular EOA into an AirAccount-managed wallet.
export const AirAccountDelegateABI = (AirAccountDelegateABIData as any).abi || AirAccountDelegateABIData;
export const AirAccountDelegateArtifact = AirAccountDelegateABIData;

// AAStar validator router — algId → algorithm-contract dispatch. NOT the same as
// SuperPaymaster's BLSValidator.json above.
export const AAStarValidatorABI = (AAStarValidatorABIData as any).abi || AAStarValidatorABIData;
export const AAStarValidatorArtifact = AAStarValidatorABIData;

// AirAccount BLS primitives (EIP-2537 pairing). NOT the same as SuperPaymaster's
// BLSAggregator.json / BLSValidator.json above.
export const AAStarBLSAlgorithmABI = (AAStarBLSAlgorithmABIData as any).abi || AAStarBLSAlgorithmABIData;
export const AAStarCommitteeValidatorABI =
    (AAStarCommitteeValidatorABIData as any).abi || AAStarCommitteeValidatorABIData;
export const AAStarCommitteeValidatorArtifact = AAStarCommitteeValidatorABIData;
export const AAStarBLSAlgorithmArtifact = AAStarBLSAlgorithmABIData;

export const AAStarBLSAggregatorABI = (AAStarBLSAggregatorABIData as any).abi || AAStarBLSAggregatorABIData;
export const AAStarBLSAggregatorArtifact = AAStarBLSAggregatorABIData;

// DeFi calldata parser registry — parsers OPT-IN per account via account.setParserRegistry.
// v0.17.2-beta.1 KI-14: Railgun + Uniswap parser NOT deployed (fail-open bug); only the
// registry stub is on-chain.
export const CalldataParserRegistryABI = (CalldataParserRegistryABIData as any).abi || CalldataParserRegistryABIData;
export const CalldataParserRegistryArtifact = CalldataParserRegistryABIData;

// Diamond-lite fallback target for AAStarAirAccountV7 — agent (ERC-8004) + weighted-signature
// governance. Reached via account's fallback->delegatecall. Standalone ABI included for tooling
// that wants to encode calls without going through the merged V7 ABI.
export const AirAccountExtensionABI = (AirAccountExtensionABIData as any).abi || AirAccountExtensionABIData;
export const AirAccountExtensionArtifact = AirAccountExtensionABIData;

// Generic ERC-20 — used for payment-token (USDC/USDT) balance/allowance/approve in the
// launch token-sale flow. EIP-3009 gasless transfer is signed via signTypedData (no ABI).
export const ERC20ABI = (ERC20ABIData as any).abi || ERC20ABIData;

// MushroomDAO launch token-sale stack (aPoints + governance-token sale).
// SaleContractV2: sells the constructor-bound GToken (getCurrentPriceUSD/getTokensForUSD/buyTokens,
//   `gToken()` immutable getter). APNTsSaleContract: sells bound aPNTs (priceUSD/getAPNTsForUSD/
//   buyAPNTs, `aPNTs()` getter). BuyHelper: gasless EIP-3009 + EIP-712 BuyIntent entrypoint, exposes
//   USDC/GTOKEN/APNTS/SALE_GT/SALE_AP immutables. The bound payout token is read on-chain (never
//   duplicated in the address book). Source: MushroomDAO/launch contracts/out/*.json.
export const SaleContractV2ABI = (SaleContractV2ABIData as any).abi || SaleContractV2ABIData;
export const APNTsSaleContractABI = (APNTsSaleContractABIData as any).abi || APNTsSaleContractABIData;
export const BuyHelperABI = (BuyHelperABIData as any).abi || BuyHelperABIData;
